/**
 * Generating a map, and looking at it before it exists.
 *
 * Acceptance criteria 10 and 11 are one screen: *the AI produces a preview*, and *the user can
 * reject it*. Both are true here for the same structural reason — what comes back from the Worker
 * is a value, this component holds it in state, and accepting is the only thing that hands it to
 * anybody. Rejecting is `setPhase({ status: 'asking' })`, which cannot fail, cannot half-apply and
 * needs no undo.
 *
 * **A draft is asked for, not just requested.** Beside the subject: how the map should be
 * arranged — or "let the AI choose", which picks the shape that suits the subject — how much it
 * should hold, whether its branches carry a glyph and a sentence of explanation, and anything more
 * the person wants to say about it. The choices are remembered in this browser, because somebody
 * who drafts outlines drafts outlines.
 *
 * **The preview is the map and the outline.** The picture shows the shape the chosen layout gave
 * it; the outline is where a person reads the ideas, which is what a draft is judged by. Both are
 * drawn from the document that came back, untouched, so what is on screen is what accepting
 * creates.
 *
 * **Nothing is generated without an account.** The Worker meters generations against one, and a
 * button that asked for a session somebody does not have would be a button that always fails.
 */
import { useCallback, useMemo, useState } from 'react';

import type { DraftOptions } from '@orbit-mind/api-client';
import { useLocale } from '@orbit-mind/engine-react';
import type { MapDocument } from '@orbit-mind/domain';
import type { AppError, LayoutId } from '@orbit-mind/shared-types';
import { FEATURED_LAYOUTS, layoutLabel } from '@orbit-mind/template-catalog';
import type { OutlineNode } from '@orbit-mind/validators';

import { useCanvasFontFamily } from '../canvas/measurer';
import { count } from '../telemetry/telemetry';

import { generate, recoverGeneration, type GeneratedProposal } from './generateApi';
import { layoutPreviewSrc } from './layoutPreview';
import { templatePreviewSrc } from './templatePreview';
import { Button, SwitchRow } from '../ui/controls';
import { IconOrbit, IconPlus } from '../ui/icons';

export interface GeneratePanelProps {
  /** False when nobody is signed in: the panel says so instead of offering a button that fails. */
  readonly available: boolean;
  readonly busy: boolean;
  readonly onAccept: (document: MapDocument) => void;
}

type Detail = NonNullable<DraftOptions['detail']>;

/** Everything a draft can be asked for, with a value for each. */
interface Choices {
  readonly layout: LayoutId | 'auto';
  readonly detail: Detail;
  readonly emoji: boolean;
  readonly notes: boolean;
}

type Phase =
  | { readonly status: 'asking' }
  | { readonly status: 'generating' }
  | {
      readonly status: 'proposed';
      readonly proposal: GeneratedProposal;
      readonly options: DraftOptions;
    }
  | { readonly status: 'failed'; readonly error: AppError; readonly subject: string };

const DEFAULT_CHOICES: Choices = { layout: 'auto', detail: 'standard', emoji: true, notes: false };

const LAYOUT_CHOICES: readonly { readonly id: LayoutId | 'auto'; readonly label: string }[] = [
  { id: 'auto', label: 'Let AI choose' },
  ...FEATURED_LAYOUTS,
];

const DETAILS: readonly { readonly id: Detail; readonly label: string; readonly hint: string }[] = [
  { id: 'brief', label: 'Brief', hint: 'A quick sketch' },
  { id: 'standard', label: 'Standard', hint: 'A full map' },
  { id: 'detailed', label: 'Detailed', hint: 'Goes deeper' },
];

/** The most a person may add about what they want. The Worker holds the same line. */
const MAX_CONTEXT = 1000;

const CHOICES_KEY = 'orbit-mind.draft-choices';

/** The choices this browser made last time, or the defaults. Never trusted further than a guess. */
function rememberedChoices(): Choices {
  try {
    const raw = window.localStorage.getItem(CHOICES_KEY);
    if (raw === null) return DEFAULT_CHOICES;
    const stored = JSON.parse(raw) as Partial<Record<keyof Choices, unknown>>;
    return {
      layout: LAYOUT_CHOICES.some((choice) => choice.id === stored.layout)
        ? (stored.layout as Choices['layout'])
        : DEFAULT_CHOICES.layout,
      detail: DETAILS.some((detail) => detail.id === stored.detail)
        ? (stored.detail as Detail)
        : DEFAULT_CHOICES.detail,
      emoji: typeof stored.emoji === 'boolean' ? stored.emoji : DEFAULT_CHOICES.emoji,
      notes: typeof stored.notes === 'boolean' ? stored.notes : DEFAULT_CHOICES.notes,
    };
  } catch {
    return DEFAULT_CHOICES;
  }
}

function rememberChoices(choices: Choices): void {
  try {
    window.localStorage.setItem(CHOICES_KEY, JSON.stringify(choices));
  } catch {
    // Private mode: the choices simply start from the defaults next time.
  }
}

export function GeneratePanel({
  available,
  busy,
  onAccept,
}: GeneratePanelProps): React.JSX.Element {
  const { text: t } = useLocale();
  const [recovering, setRecovering] = useState(false);
  const [subject, setSubject] = useState('');
  const [context, setContext] = useState('');
  const [addingContext, setAddingContext] = useState(false);
  const [choices, setChoices] = useState<Choices>(rememberedChoices);
  const [phase, setPhase] = useState<Phase>({ status: 'asking' });

  const choose = (change: Partial<Choices>): void => {
    setChoices((current) => {
      const next = { ...current, ...change };
      rememberChoices(next);
      return next;
    });
  };

  const run = useCallback(async (asked: string, options: DraftOptions): Promise<void> => {
    setPhase({ status: 'generating' });
    const startedAt = Date.now();
    const answered = await generate(asked, options);

    // How long a model took is the number that decides whether generating feels broken, and it is
    // the one thing about a draft worth measuring — the subject is never sent anywhere.
    count({ kind: 'timing', event: 'draft-generated', ms: Date.now() - startedAt });
    if (answered.ok) count({ kind: 'funnel', event: 'draft-previewed' });

    setPhase(
      answered.ok
        ? { status: 'proposed', proposal: answered.value, options }
        : { status: 'failed', error: answered.error, subject: asked },
    );
  }, []);

  if (!available) {
    return (
      <div className="grid gap-3 p-5 sm:p-8">
        <p className="font-medium">Generating a map needs an account.</p>
        <p className="text-ink-muted text-sm">
          Sign in from the library and this tab will ask a model to draft a map for you. What it
          suggests is shown here first — nothing is created until you say so.
        </p>
      </div>
    );
  }

  if (phase.status === 'proposed') {
    return (
      <Proposal
        proposal={phase.proposal}
        busy={busy}
        onDiscard={() => {
          // The gap between previewed and accepted is the whole quality signal for drafting,
          // which is why a discard is counted rather than treated as an absence.
          count({ kind: 'funnel', event: 'draft-discarded' });
          setPhase({ status: 'asking' });
        }}
        onRetry={() => void run(phase.proposal.prompt, phase.options)}
        onAccept={() => {
          count({ kind: 'funnel', event: 'draft-accepted' });
          onAccept(phase.proposal.document);
        }}
      />
    );
  }

  const generating = phase.status === 'generating';
  const options: DraftOptions = {
    layout: choices.layout,
    detail: choices.detail,
    emoji: choices.emoji,
    notes: choices.notes,
    ...(context.trim() === '' ? {} : { context: context.trim() }),
  };

  return (
    <form
      className="flex min-h-0 flex-1 flex-col"
      onSubmit={(event) => {
        event.preventDefault();
        if (subject.trim().length > 0 && !generating) void run(subject, options);
      }}
    >
      <div className="grid min-h-0 flex-1 content-start gap-5 overflow-y-auto overscroll-contain p-4 sm:p-6">
        <Button
          disabled={generating || busy || recovering}
          onClick={() => {
            setRecovering(true);
            void recoverGeneration()
              .then((result) => {
                if (result.ok && result.value !== null)
                  setPhase({ status: 'proposed', proposal: result.value, options });
                else if (!result.ok) setPhase({ status: 'failed', error: result.error, subject });
                else
                  window.alert(
                    t(
                      'No recoverable draft in the last 24 hours.',
                      'Nenhum rascunho recuperável nas últimas 24 horas.',
                    ),
                  );
              })
              .finally(() => {
                setRecovering(false);
              });
          }}
        >
          {t(
            'Recover last AI draft · 24h · no new charge',
            'Recuperar último rascunho de IA · 24h · sem nova cobrança',
          )}
        </Button>
        <p className="text-ink-muted text-xs">
          {t(
            'Completed drafts are kept privately for 24 hours for recovery. Your original request is not saved separately.',
            'Rascunhos concluídos ficam privados por 24 horas para recuperação. O pedido original não é salvo separadamente.',
          )}
        </p>
        <div className="grid gap-2">
          <label className="text-sm font-medium" htmlFor="ai-subject">
            What should the map be about?
          </label>
          <input
            id="ai-subject"
            data-generate-subject
            value={subject}
            onChange={(event) => {
              setSubject(event.target.value);
            }}
            placeholder="the water cycle, for a ten-year-old"
            maxLength={500}
            disabled={generating}
            className="border-edge bg-surface text-ink focus:border-edge-strong h-11 rounded-lg border px-3 text-sm outline-none"
          />
          {addingContext ? (
            <textarea
              aria-label="More about what you want"
              value={context}
              onChange={(event) => {
                setContext(event.target.value);
              }}
              placeholder="Who it is for, what it is for, anything it must include"
              maxLength={MAX_CONTEXT}
              rows={3}
              disabled={generating}
              className="border-edge bg-surface text-ink focus:border-edge-strong rounded-lg border px-3 py-2 text-sm outline-none"
            />
          ) : (
            <button
              type="button"
              onClick={() => {
                setAddingContext(true);
              }}
              className="text-accent justify-self-start text-xs font-medium hover:underline"
            >
              + Add details (optional)
            </button>
          )}
        </div>

        <fieldset className="grid gap-2">
          <legend className="mb-2 text-sm font-medium">Layout</legend>
          <div
            role="radiogroup"
            aria-label="Layout"
            className="grid grid-cols-3 gap-2 sm:grid-cols-5"
          >
            {LAYOUT_CHOICES.map((choice) => (
              <LayoutChoice
                key={choice.id}
                id={choice.id}
                label={choice.label}
                checked={choices.layout === choice.id}
                onChoose={() => {
                  choose({ layout: choice.id });
                }}
              />
            ))}
          </div>
        </fieldset>

        <fieldset className="grid gap-2">
          <legend className="mb-2 text-sm font-medium">Detail</legend>
          <div
            role="radiogroup"
            aria-label="Detail"
            className="border-edge grid grid-cols-3 gap-1 rounded-xl border p-1"
          >
            {DETAILS.map((detail) => (
              <button
                key={detail.id}
                type="button"
                role="radio"
                aria-checked={choices.detail === detail.id}
                aria-label={detail.label}
                onClick={() => {
                  choose({ detail: detail.id });
                }}
                className={`rounded-lg px-2 py-2 text-center transition-colors ${
                  choices.detail === detail.id
                    ? 'bg-accent-soft text-accent'
                    : 'text-ink-muted hover:text-ink'
                }`}
              >
                <span className="block text-sm font-medium">{detail.label}</span>
                <span className="block text-[0.6875rem] opacity-80">{detail.hint}</span>
              </button>
            ))}
          </div>
        </fieldset>

        <div className="grid gap-3">
          <SwitchRow
            id="draft-emoji"
            checked={choices.emoji}
            onChange={(emoji) => {
              choose({ emoji });
            }}
          >
            An emoji on each main branch
          </SwitchRow>
          <SwitchRow
            id="draft-notes"
            checked={choices.notes}
            onChange={(notes) => {
              choose({ notes });
            }}
          >
            A note explaining each main branch
          </SwitchRow>
        </div>

        {phase.status === 'failed' && (
          <p className="border-danger/50 rounded-panel bg-surface border p-3 text-sm">
            {phase.error.message}
          </p>
        )}
      </div>

      <div className="border-edge flex shrink-0 flex-wrap items-center justify-between gap-3 border-t px-4 py-3 sm:px-6">
        <p className="text-ink-faint flex items-center gap-2 text-xs">
          <IconOrbit size={14} className="text-accent shrink-0" />
          You will see what it suggests before anything is created.
        </p>
        <Button
          tone="primary"
          size="lg"
          type="submit"
          disabled={generating || subject.trim().length === 0}
        >
          {generating ? 'Drafting…' : 'Draft a map'}
        </Button>
      </div>
    </form>
  );
}

/** One way to arrange the draft, as a small picture of it — or the AI's own choice. */
function LayoutChoice({
  id,
  label,
  checked,
  onChoose,
}: {
  id: LayoutId | 'auto';
  label: string;
  checked: boolean;
  onChoose: () => void;
}): React.JSX.Element {
  const fontFamily = useCanvasFontFamily();
  const preview = useMemo(
    () => (id === 'auto' ? null : layoutPreviewSrc(id, fontFamily)),
    [id, fontFamily],
  );

  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      aria-label={label}
      onClick={onChoose}
      className={`rounded-panel flex flex-col overflow-hidden border text-left transition-[border-color,box-shadow] ${
        checked
          ? 'border-accent ring-accent/30 shadow-md ring-2'
          : 'border-edge hover:border-edge-strong'
      }`}
    >
      <span
        className="flex h-12 w-full items-center justify-center"
        style={{ backgroundColor: '#f4f5f8' }}
      >
        {preview === null ? (
          <IconOrbit size={22} className="text-accent" />
        ) : (
          <img src={preview} alt="" className="h-full w-full object-contain p-1" />
        )}
      </span>
      <span
        className={`block truncate px-2 py-1 text-[0.6875rem] font-medium ${checked ? 'text-accent' : ''}`}
      >
        {label}
      </span>
    </button>
  );
}

/** A draft, shown two ways — its shape and its ideas — with the three things to do about it. */
function Proposal({
  proposal,
  busy,
  onDiscard,
  onRetry,
  onAccept,
}: {
  proposal: GeneratedProposal;
  busy: boolean;
  onDiscard: () => void;
  onRetry: () => void;
  onAccept: () => void;
}): React.JSX.Element {
  const fontFamily = useCanvasFontFamily();
  const picture = useMemo(
    () => templatePreviewSrc(proposal.document, fontFamily),
    [proposal.document, fontFamily],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
        {/* The same sheet of paper the template cards use: this is a picture of a map. */}
        <div
          className="rounded-panel border-edge mb-4 h-44 overflow-hidden border sm:h-56"
          style={{ backgroundColor: '#f4f5f8' }}
        >
          <img src={picture} alt="" className="h-full w-full object-contain p-3" />
        </div>

        <p className="text-ink-muted mb-1 text-xs">
          {proposal.summary.nodeCount === 1
            ? '1 node'
            : `${String(proposal.summary.nodeCount)} nodes`}{' '}
          · drafted by {proposal.model}
          {proposal.summary.trimmed ? ' · shortened to fit a map' : ''}
          {` · ${layoutLabel(proposal.document.map.layoutId)}`}
        </p>
        <h3 className="mb-3 text-lg font-semibold">{proposal.outline.title}</h3>

        {/* The outline the model produced, which is where a person reads ideas. */}
        <ul className="border-edge border-l pl-4">
          <OutlineLevel nodes={proposal.outline.nodes} />
        </ul>
      </div>

      <div className="border-edge flex shrink-0 flex-wrap items-center justify-end gap-2 border-t p-4">
        {/*
          Rejecting comes first and is not a destructive-looking control, because it is not
          destructive: nothing exists yet, and this is the ordinary way out of a draft that is
          not what somebody meant.
        */}
        <Button onClick={onDiscard} disabled={busy}>
          Discard
        </Button>
        <Button onClick={onRetry} disabled={busy}>
          Try again
        </Button>
        <Button tone="primary" size="lg" disabled={busy} onClick={onAccept}>
          <IconPlus size={18} />
          Create this map
        </Button>
      </div>
    </div>
  );
}

/** The outline, nested. A list of lists, which is what an outline is. */
function OutlineLevel({ nodes }: { nodes: readonly OutlineNode[] }): React.JSX.Element {
  return (
    <>
      {nodes.map((node, index) => (
        <li key={`${node.text}-${String(index)}`} className="py-0.5 text-sm">
          {node.emoji !== undefined && (
            <span className="mr-1.5" aria-hidden="true">
              {node.emoji}
            </span>
          )}
          <span>{node.text}</span>
          {node.note !== undefined && (
            <span className="text-ink-muted block text-xs">{node.note}</span>
          )}
          {node.children.length > 0 && (
            <ul className="border-edge mt-0.5 border-l pl-4">
              <OutlineLevel nodes={node.children} />
            </ul>
          )}
        </li>
      ))}
    </>
  );
}
